/**
 * PanelNumberField — the slider's readout, typed into.
 *
 * A readout you can only drag to is a readout you cannot set: the mountain-height slider steps by
 * 100 across a 15,800-wide range, so "4200" is a gesture and "4237" is impossible. This is the same
 * number, editable.
 *
 * IT HOLDS THE TEXT WHILE YOU TYPE AND COMMITS ON THE WAY OUT. Parsing every keystraw straight into
 * the config makes the intermediate states unreachable — clearing the box to type a new value reads
 * as NaN, "4." reads as 4 and then fights the cursor, and a leading digit below `min` gets clamped
 * before you finish typing the rest of it. So the string is local state, and blur or Enter is what
 * parses, clamps and writes. Escape puts the committed value back.
 *
 * CLAMPING IS ON COMMIT ONLY, and to the parameter's own range — `clampParam`'s note says the range
 * is what the UI offers rather than what the generator accepts, and this is the UI.
 */

import { useEffect, useRef, useState } from 'react';
import { MOTION, PARAM_UNIT_W, PARAM_VALUE_W, TRACK } from './tokens';
import { formatForStep } from './PanelNumberRow';

/**
 * What the typed text becomes when it is committed. Pure, and tested — because every wrong answer
 * here writes a number into the world config: NaN from an emptied box, an unclamped 99999 from a
 * fat finger, `Infinity` from "1e999". Anything that does not parse leaves the value alone.
 */
export function commitNumber(text: string, current: number, min: number, max: number): number {
  const parsed = parseFloat(text);
  if (!Number.isFinite(parsed)) return current;
  return Math.min(max, Math.max(min, parsed));
}

interface PanelNumberFieldProps {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  unit?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

export function PanelNumberField({
  value, min, max, step, onChange, unit, disabled, ariaLabel,
}: PanelNumberFieldProps) {
  const [text, setText] = useState(() => formatForStep(value, step));
  const editing = useRef(false);

  // Follow the value while the box is not being edited (the slider moves it, a reset moves it).
  useEffect(() => {
    if (!editing.current) setText(formatForStep(value, step));
  }, [value, step]);

  const commit = () => {
    editing.current = false;
    const next = commitNumber(text, value, min, max);
    setText(formatForStep(next, step));
    if (next !== value) onChange(next);
  };

  return (
    <div className={`flex items-center gap-1 shrink-0 ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
      <input
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        value={text}
        disabled={disabled}
        onFocus={(e) => { editing.current = true; e.currentTarget.select(); }}
        onChange={(e) => { editing.current = true; setText(e.target.value); }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { commit(); e.currentTarget.blur(); }
          if (e.key === 'Escape') { editing.current = false; setText(formatForStep(value, step)); e.currentTarget.blur(); }
        }}
        className={`${PARAM_VALUE_W} h-7 ${TRACK} px-1.5 text-[11px] font-bold tabular-nums text-right
          text-neutral-800 outline-none focus:ring-1 focus:ring-[#93CB55] ${MOTION}`}
      />
      {/* A fixed column whether or not there IS a unit, or the value boxes of a `x` row and a `%`
          row stop on different pixels. */}
      <span className={`${PARAM_UNIT_W} shrink-0 text-[9px] font-semibold text-neutral-400`}>{unit}</span>
    </div>
  );
}
